import { create } from 'zustand';
import { api } from '@/lib/api';

export interface NotificationItem {
  id: number;
  read: boolean;
  readAt: string | null;
  createdAt: string;
  notificationId: number;
  title: string;
  body: string;
  url: string | null;
  icon: string | null;
  sentAt: string | null;
}

interface NotificationState {
  notifications: NotificationItem[];
  unreadCount: number;
  isLoading: boolean;
  hasMore: boolean;
  page: number;

  fetchUnreadCount: () => Promise<void>;
  fetchNotifications: (page?: number) => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  reset: () => void;
}

export const useNotificationStore = create<NotificationState>((set) => ({
  notifications: [],
  unreadCount: 0,
  isLoading: false,
  hasMore: true,
  page: 1,

  fetchUnreadCount: async () => {
    try {
      const res = await api.getUnreadNotificationCount();
      if (res.success && res.data) {
        set({ unreadCount: res.data.count });
      }
    } catch {}
  },

  fetchNotifications: async (page = 1) => {
    set({ isLoading: true });
    try {
      const res = await api.getNotifications(page);
      if (res.success && res.data) {
        const data = res.data;
        set((state) => ({
          notifications:
            page === 1
              ? data.notifications
              : [...state.notifications, ...data.notifications],
          hasMore: data.hasMore,
          page,
          isLoading: false,
        }));
      }
    } catch {
      set({ isLoading: false });
    }
  },

  markAsRead: async (id: number) => {
    try {
      await api.markNotificationRead(id);
      set((state) => ({
        notifications: state.notifications.map((n) =>
          n.id === id ? { ...n, read: true, readAt: new Date().toISOString() } : n
        ),
        unreadCount: Math.max(0, state.unreadCount - 1),
      }));
    } catch {}
  },

  markAllAsRead: async () => {
    try {
      await api.markAllNotificationsRead();
      set((state) => ({
        notifications: state.notifications.map((n) => ({
          ...n,
          read: true,
          readAt: new Date().toISOString(),
        })),
        unreadCount: 0,
      }));
    } catch {}
  },

  reset: () => {
    set({ notifications: [], unreadCount: 0, isLoading: false, hasMore: true, page: 1 });
  },
}));
